/**
 * Builds the `invitation_outcome` event — what the server actually did with an
 * accept or decline, as opposed to `invitation_clicked`, which records only
 * that the user pressed the button.
 *
 * Fired from the store rather than from a component, deliberately. The accept
 * path funnels every flavour through `processInvitationToken`, which is also
 * where the outcome becomes known; the invitations list dispatches into it and
 * then returns, having already marked the invite accepted locally. A component
 * firing this event would be reporting its own optimism.
 *
 * `errorCodeForStatus` maps HTTP status to the coarse bucket the event carries.
 * The mapping mirrors the toasts in `invitationActions`: 401/403 is the "you
 * are signed in as the wrong person" case, 422 is an expired or already-used
 * token, and anything else is bucketed rather than guessed at.
 */

import { getSessionId } from "./session";
import type {
  InvitationErrorCode,
  InvitationOutcomeEvent,
  InvitationOutcomeProperties,
  InvitationOutcomeType,
} from "./types";

interface InvitationOutcomeInput {
  inviteType: InvitationOutcomeType;
  action: InvitationOutcomeProperties["action"];
  outcome: InvitationOutcomeProperties["outcome"];
  statusCode?: number | null;
  errorCode?: InvitationErrorCode | null;
  inviteId?: number | null;
  hubId?: number | null;
  projectId?: number | null;
}

/** Coarse bucket for an HTTP status. `null` status means the request never landed. */
export const errorCodeForStatus = (status: number | null | undefined): InvitationErrorCode => {
  switch (status) {
    case 401:
    case 403:
      return "wrong_account";
    case 422:
      return "expired_or_invalid";
    case 404:
      return "not_found";
    default:
      return "unknown";
  }
};

export const buildInvitationOutcomeEvent = ({
  inviteType,
  action,
  outcome,
  statusCode = null,
  errorCode = null,
  inviteId = null,
  hubId = null,
  projectId = null,
}: InvitationOutcomeInput): InvitationOutcomeEvent => ({
  name: "invitation_outcome",
  occurred_at: new Date().toISOString(),
  session_id: getSessionId(),
  hub_id: hubId,
  project_id: projectId,
  properties: {
    invite_type: inviteType,
    action,
    outcome,
    status_code: statusCode,
    error_code: outcome === "error" ? (errorCode ?? errorCodeForStatus(statusCode)) : null,
    invite_id: inviteId,
  },
});
